import api from '@/api/axiosConfig.js';

export function isChatModuleUnavailableError(error) {
  const status = Number(error?.response?.status || 0);
  const requestUrl = String(error?.config?.url || '');
  return status === 404 && requestUrl.includes('/api/chat');
}

export function getChatErrorMessage(error, fallback) {
  if (isChatModuleUnavailableError(error)) {
    return 'El backend de chat no esta disponible en este entorno.';
  }

  return error?.response?.data?.message || fallback;
}

export async function fetchChatBootstrap() {
  try {
    const response = await api.get('/api/chat/bootstrap');
    return response.data?.data ?? {
      me: null,
      contacts: [],
      conversations: [],
      onlineWindowSeconds: 90,
      capabilities: null,
      chatUnavailable: false,
    };
  } catch (error) {
    if (isChatModuleUnavailableError(error)) {
      return {
        me: null,
        contacts: [],
        conversations: [],
        onlineWindowSeconds: 90,
        capabilities: null,
        chatUnavailable: true,
      };
    }
    throw error;
  }
}

export async function sendChatHeartbeat(status = 'online') {
  try {
    const response = await api.post(
      '/api/chat/presence/heartbeat',
      { status },
      { skipAuthRedirect: true, timeout: 10000 },
    );
    return response.data?.data ?? null;
  } catch (error) {
    if (isChatModuleUnavailableError(error)) {
      return null;
    }
    throw error;
  }
}

export async function openDirectConversation(participantId) {
  const response = await api.post('/api/chat/conversations/direct', { participantId });
  return response.data?.data ?? null;
}

export async function fetchConversationMessages(conversationId, limit = 60) {
  const response = await api.get(`/api/chat/conversations/${conversationId}/messages`, {
    params: { limit },
  });
  const payload = response.data?.data;
  if (Array.isArray(payload)) {
    return {
      items: payload,
      pagination: {
        limit,
        beforeMessageId: null,
        oldestMessageId: payload[0]?.id ?? null,
        newestMessageId: payload[payload.length - 1]?.id ?? null,
        nextBeforeMessageId: null,
        hasMore: false,
      },
    };
  }
  return payload ?? {
    items: [],
    pagination: null,
  };
}

export async function sendConversationMessage(conversationId, payload) {
  const response = await api.post(`/api/chat/conversations/${conversationId}/messages`, payload, {
    timeout: 60000,
  });
  return response.data?.data ?? null;
}

export async function markConversationAsRead(conversationId) {
  const response = await api.post(`/api/chat/conversations/${conversationId}/read`);
  return response.data?.data ?? null;
}

export function resolveChatAssetUrl(resourceUrl) {
  const rawUrl = String(resourceUrl || '').trim();
  if (!rawUrl) return '';
  if (/^https?:\/\//i.test(rawUrl)) {
    return rawUrl;
  }
  return new URL(rawUrl, api.defaults.baseURL).toString();
}

export async function openChatAttachment(attachment) {
  const attachmentUrl = attachment?.downloadUrl || attachment?.url;
  const requestUrl = resolveChatAssetUrl(attachmentUrl);

  if (!requestUrl) {
    throw new Error('No pudimos resolver el adjunto.');
  }

  const response = await api.get(requestUrl, {
    responseType: 'blob',
    timeout: 60000,
  });

  const fileName = attachment?.nombre || 'adjunto';
  const objectUrl = window.URL.createObjectURL(response.data);
  const nextWindow = window.open(objectUrl, '_blank', 'noopener,noreferrer');

  if (!nextWindow) {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    link.rel = 'noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  window.setTimeout(() => {
    window.URL.revokeObjectURL(objectUrl);
  }, 60000);
}
